import type { ApiClient } from '@carbonwebmail/api-client'
import type { BulkEditRequest, ContactPageDto, ContactSortDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import type { AsyncState } from '../../ui/Async.js'
import { useAction } from '../../ui/useAction.js'
import { useSelection } from '../../ui/useSelection.js'
import type { Rule } from '../segments/ruleVocabulary.js'
import type { KeyedRule } from '../segments/useSegmentBuilder.js'

/**
 * The contacts list: its saved view, its search, what is ticked, and what can
 * be done to it.
 *
 * **The chosen view lives in the url.** It is a tab on the board above, so a
 * refresh, a back button and a pasted link have to land on the same rows —
 * which they cannot if the only record of it is a variable in a component. The
 * views themselves are `useContactViews`.
 *
 * **The selection is per page and is cleared whenever the rows change.** A tick
 * that survived a page turn would mean the toolbar counted people no longer on
 * screen, and the actions it offers are not ones to perform on a list somebody
 * cannot see.
 *
 * **"Everybody matching" is a separate state from the ticks**, because it is a
 * different kind of thing: an action against a filter, which travels and is
 * resolved by the server, rather than against rows. Only connecting a segment
 * takes it. Deleting and setting a column take the ticks alone — see
 * `ContactsList`.
 *
 * `empty` and `no-matches` stay separate outcomes rather than one "nothing to
 * show", because they need opposite things said. A tenant with no contacts
 * needs to be told how to get some; a filter that matched nothing needs
 * clearing.
 */

export type ContactsState = AsyncState<ContactPageDto>

interface UseContactsOptions {
  readonly client: ApiClient
}

/**
 * Asking for the save-a-view dialog, in the url.
 *
 * The one piece of dialog state here that is not local, and for a reason: the
 * `+` on the tab strip belongs to `ContactsBoard`, which does not hold the
 * filter, the order or the hidden columns that a view *is*. The url is the one
 * channel the two screens already share, so the button asks and this screen —
 * which has all three — opens it. Both ways in set the same thing, because two
 * sources of truth for one dialog is one that opens twice or not at all.
 */
export const NEW_VIEW = 'new-view'

export function useContacts({ client }: UseContactsOptions) {
  const [params, setParams] = useSearchParams()
  const view = params.get('view') ?? undefined
  const savingView = params.has(NEW_VIEW)

  const [search, setSearchValue] = useState('')
  /**
   * Which column the rows are ordered by, and which columns are drawn.
   *
   * The sort goes to the server — the board shows a page out of however many
   * there are, so ordering the page would answer a question about the page.
   * What is hidden does not: that is about this screen and nothing else, and no
   * request should be made to answer it.
   */
  const [sort, setSortValue] = useState<ContactSortDto | undefined>(undefined)
  /**
   * Which columns this person has hidden, or undefined if they have not said.
   *
   * Undefined rather than an empty set, because "I have hidden nothing" and "I
   * have not touched this, use the view's" are different answers and an empty
   * set cannot tell them apart. The view's own hidden columns apply until
   * somebody overrides them, and then theirs do — for this visit only.
   */
  const [hidden, setHiddenValue] = useState<ReadonlySet<string> | undefined>(undefined)
  /**
   * Extra rules on top of the view, which is where the board's own filter goes.
   *
   * Not in the url, unlike the sort. A rule tree does not fit one at any useful
   * size, and its values are somebody's data — an address fragment in a url is
   * an address fragment in browser history and in every log on the way. Saving
   * it as a view is what makes it last, and a view has a url of its own.
   */
  const [rules, setRulesValue] = useState<readonly KeyedRule[]>([])

  /**
   * What the rules *say*, not which objects they are.
   *
   * The list is rebuilt on every edit, so an effect keyed on it would re-ask
   * the server when nothing had changed — and its own answer arriving is a
   * render, which is how a board ends up querying in a loop. The same reason
   * `usePreview` keys on its body's contents.
   */
  const asked = JSON.stringify(rules.map((one) => one.rule))
  const [page, setPage] = useState(1)
  const [state, setState] = useState<ContactsState>({ status: 'loading' })
  /** A later page is on its way. Distinct from loading, which blanks the table. */
  const [appending, setAppending] = useState(false)
  const selection = useSelection()
  const { picked } = selection
  /**
   * Everybody the filter matches, rather than everybody ticked.
   *
   * A separate state from the ticks because it is a different kind of thing: an
   * action against a *filter* rather than against a selection. The count comes
   * from the server's total, the rows are never fetched, and the request
   * carries the filter rather than the ids — a browser that expanded forty
   * thousand rows would be sending a list that had already moved.
   */
  const [all, setAll] = useState(false)

  /**
   * The rows on screen, which are every page fetched so far rather than one.
   *
   * **The board shows the whole list and you scroll it.** Fifty at a time with
   * Previous and Next meant an organisation with four thousand contacts had
   * eighty pages to walk, and the search and the filters — which are how
   * somebody actually finds a person — could only narrow the page they were
   * looking at into something smaller still.
   *
   * They are *accumulated* rather than fetched in one request, because a table
   * of forty thousand rows is a browser that stops responding. The server keeps
   * paging; the screen keeps what it has already been given and asks for more
   * when the bottom comes into view.
   */
  const load = useCallback(async () => {
    const children = JSON.parse(asked) as Rule[]
    const first = page === 1

    // Only the first page replaces. Appending must not blank the table, or
    // scrolling would flash the whole list away and back on every fetch —
    // which is why 'fetching more' is its own flag rather than the loading
    // state the shared AsyncState offers.
    if (first) setState({ status: 'loading' })
    setAppending(!first)

    if (first) {
      // Whatever was ticked belonged to the rows that are about to be replaced,
      // and "everybody matching" belonged to the filter that is about to change.
      // Not on a later page: a tick must survive scrolling to the next.
      selection.clear()
      setAll(false)
    }

    try {
      const answer = await client.queryContacts({
        search,
        ...(view === undefined ? {} : { view }),
        ...(children.length === 0 ? {} : { filter: { match: 'all' as const, children } }),
        ...(sort === undefined ? {} : { sort }),
        page,
      })

      setState((current) => ({
        status: 'ready',
        value:
          first || current.status !== 'ready'
            ? answer
            : { ...answer, contacts: [...current.value.contacts, ...answer.contacts] },
      }))
    } catch {
      setState({
        status: 'failed',
        detail: 'We could not load your contacts. Please try again in a moment.',
      })
    } finally {
      setAppending(false)
    }
  }, [asked, client, page, search, selection.clear, sort, view])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Asks for the next page, when there is one and nothing is in flight.
   *
   * Called by the screen when the end of the table comes into view. Guarded
   * here rather than there: "is there more" is a fact about what the server
   * said, and a scroll listener that had to work it out would get it wrong
   * every time a fetch was already running.
   */
  const more = useCallback(() => {
    if (state.status !== 'ready' || appending) return
    if (state.value.contacts.length >= state.value.total) return
    setPage((current) => current + 1)
  }, [appending, state])

  /**
   * Starts the list again from the top.
   *
   * **Not `load` any more.** Once rows accumulate, `load` fetches whatever page
   * is current and *appends* it — so after a bulk action it left every stale row
   * on screen and added a page underneath. Fixing four thousand names changed
   * every one of them in the database and not one of them on the board.
   *
   * Changing the page re-runs the effect; when it is already one, nothing would
   * change and the effect would not fire, so that case asks directly.
   */
  const retry = useCallback(() => {
    if (page === 1) void load()
    else setPage(1)
  }, [load, page])

  /** Whether anything is left to fetch, so the screen can say so rather than guess. */
  const everything = state.status === 'ready' && state.value.contacts.length >= state.value.total

  /**
   * A narrower list is a different page one, the same as a new search.
   *
   * The rules are held with a key each because they are edited in place and
   * React needs to tell one row from another — a key made from the row's
   * position is the thing keys exist to avoid. The keys never leave this hook.
   */
  const minted = useRef(0)
  const setRules = useCallback((next: readonly KeyedRule[]) => {
    setRulesValue(next)
    setPage(1)
  }, [])

  const newRule = useCallback((rule: Rule): KeyedRule => {
    minted.current += 1
    return { key: `r${minted.current}`, rule }
  }, [])

  /** A different order is a different page one. Page 4 of the old order means nothing. */
  const setSort = useCallback((next: ContactSortDto | undefined) => {
    setSortValue(next)
    setPage(1)
  }, [])

  /** A new filter over the old page number lands somebody on page 4 of a two-page result. */
  const setSearch = useCallback((value: string) => {
    setSearchValue(value)
    setPage(1)
  }, [])

  /**
   * Leaving the chosen view: `/contacts` with no view is everyone, and no tab
   * is current. There is no All tab to return to — All is a segment like the
   * rest, and can have been deleted.
   */
  const clearView = useCallback(() => {
    setParams({})
    setPage(1)
  }, [setParams])

  /**
   * Opens or closes the save-a-view dialog, keeping the view that is open.
   *
   * The rows must not move while somebody is naming an arrangement of them,
   * which is what dropping `view` here would do.
   */
  const setSavingView = useCallback(
    (asked: boolean) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (asked) next.set(NEW_VIEW, '')
          else next.delete(NEW_VIEW)
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  /*
   * Ticking is `useSelection`, shared with the segments board — the rules are
   * the same whatever the rows are. What is added here is that touching the
   * ticks leaves "everybody matching": they are two answers to one question,
   * and a board holding both at once could not say which the next action is
   * about.
   */
  const toggle = useCallback(
    (email: string) => {
      setAll(false)
      selection.toggle(email)
    },
    [selection.toggle],
  )

  const toggleAllOnPage = useCallback(
    (emails: readonly string[]) => {
      setAll(false)
      selection.toggleAllOnPage(emails)
    },
    [selection.toggleAllOnPage],
  )

  const { notice, problem, busy, run } = useAction(load)

  /**
   * Connecting contacts to a segment, or taking them off it.
   *
   * **This changes who a campaign to that segment reaches**, because a
   * segment's audience is its rules or its members. It does not override an
   * unsubscribe or a suppression: those are applied when the queue is built and
   * read again at claim (I5), and nothing here can undo them.
   *
   * The answer is how many rows actually changed, not how many were asked for.
   * Connecting sixty people when twenty are already connected changes forty,
   * and saying forty is the only way somebody can tell it worked.
   */
  const connect = useCallback(
    (segmentId: string, name: string, connecting: boolean, contactIds?: readonly string[]) =>
      run(async () => {
        const children = JSON.parse(asked) as Rule[]
        const changed = await client.changeSegmentMembers(
          segmentId,
          contactIds === undefined
            ? {
                // The board's whole question, both halves of it. The view alone
                // used to travel, so narrowing the list and then connecting
                // "everybody matching" connected everybody in the tab — the
                // rules on screen were ignored and the audience came out wrong.
                allMatching: {
                  search,
                  ...(view === undefined ? {} : { view }),
                  ...(children.length === 0 ? {} : { filter: { match: 'all' as const, children } }),
                },
              }
            : { contactIds: [...contactIds] },
          connecting,
        )

        const what = changed === 1 ? '1 contact' : `${changed} contacts`
        return connecting ? `${what} connected to "${name}".` : `${what} taken off "${name}".`
      }, 'We could not change that segment. Nothing was connected — try again.'),
    [asked, client, run, search, view],
  )

  /**
   * Setting one column on the ticked rows.
   *
   * The ticked rows only, never "all matching" — the same line delete draws,
   * and for the same reason: rewriting a column across forty thousand people
   * the server resolved after the page was drawn is not something to offer
   * behind one press.
   *
   * The answer is how many rows were set, not how many were ticked. A row
   * somebody else deleted in between contributes nothing, and saying fifty when
   * forty-nine changed is how a board stops being believed.
   */
  const edit = useCallback(
    (contactIds: readonly string[], change: BulkEditRequest['change'], column: string) =>
      run(async () => {
        const changed = await client.editContacts(contactIds, change)
        return `${changed === 1 ? '1 contact' : `${changed} contacts`} updated. ${column} is now ${
          change.value === '' ? 'empty' : `"${change.value}"`
        }.`
      }, 'We could not change those contacts. Nothing was updated — try again.'),
    [client, run],
  )

  /**
   * Deleting the ticked rows.
   *
   * Possible since migration 0028: `send_log` and `send_queue` used to cascade
   * from a contact, so removing one erased the record of every message it had
   * ever been sent. They keep the address and lose only the link.
   *
   * What it does *not* do is unsuppress anybody — the suppression list is keyed
   * on the address and has no contact on it — so deleting and importing again
   * cannot become a way to mail somebody who asked to stop. The notice says so,
   * because that is the question somebody asks immediately afterwards.
   */
  const remove = useCallback(
    (contactIds: readonly string[]) =>
      run(async () => {
        const removed = await client.deleteContacts(contactIds)
        return removed === 1
          ? '1 contact deleted. Anything still queued to them was cancelled.'
          : `${removed} contacts deleted. Anything still queued to them was cancelled.`
      }, 'We could not delete those. Nothing was removed — try again.'),
    [client, run],
  )

  /**
   * Suppressing everybody ticked, under one note.
   *
   * The note is required and applies to all of them, which is honest for the
   * case this exists for: "everyone who asked me to stop at the conference" is
   * one fact about several people. Somebody who needs a note each sends them
   * one at a time.
   */
  const suppress = useCallback(
    (note: string) =>
      run(async () => {
        const emails = [...picked]
        await client.suppress({ emails, note })
        return `${emails.length === 1 ? 'That address is' : `Those ${emails.length} addresses are`} on the suppression list. No campaign will reach them.`
      }, 'We could not suppress those addresses. Nothing was changed — try again.'),
    [client, picked, run],
  )

  return {
    search,
    view,
    sort,
    setSort,
    rules,
    setRules,
    newRule,
    hidden,
    setHidden: setHiddenValue,
    all,
    setAll,
    connect,
    page,
    state,
    more,
    everything,
    appending,
    picked,
    notice,
    problem,
    busy,
    setSearch,
    clearView,
    savingView,
    setSavingView,
    setPage,
    toggle,
    toggleAllOnPage,
    edit,
    remove,
    suppress,
    retry,
  }
}
